import React from 'react'
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Link } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import PersonOutline from '@mui/icons-material/PersonOutline'
import Logout from '@mui/icons-material/Logout'
import LockOutlined from '@mui/icons-material/LockOutlined'
import routerConstants from '@constants/routerConstants'
import { getUserInfo, logOut } from '@utils/userUtils'
import { LAYOUT_APP_BAR_HEIGHT_PX } from './layoutConstants'
import ThemePickerPopover from './ThemePickerPopover'
import HeaderBranchSwitcher from './HeaderBranchSwitcher'
import { cv } from '@theme/cssVars'
import { useCurrentRouteTitle } from 'hooks/useCurrentRouteTitle'
import logoImg from '../../assets/images/logo-nhansu.png'

const HEADER_CONTROL_FRAME_SX = {
  boxSizing: 'border-box',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '6px',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
} as const

interface HeaderMenuProps {
  onToggle?: () => void
  sidebarOpen?: boolean
}

/** Header — port gần như nguyên bản từ ytl-logistics-frontend. */
const HeaderMenu: React.FC<HeaderMenuProps> = ({ onToggle, sidebarOpen = true }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pageTitle = useCurrentRouteTitle()
  const [userAnchor, setUserAnchor] = React.useState<null | HTMLElement>(null)
  const userInfo = getUserInfo()
  const profile = userInfo?.userProfile || userInfo?.core_user_hoso || null
  const profileName = [profile?.ho, profile?.tenDem, profile?.firstName, profile?.lastName]
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part: string) => part.trim())
    .join(' ')
    .trim()
  const displayUsername =
    userInfo?.hoTen ||
    profileName ||
    userInfo?.fullName ||
    userInfo?.username ||
    userInfo?.tenDangNhap ||
    'Tài khoản'
  // Chỉ hiện role thật; không fallback "Người dùng" (dễ hiểu nhầm là tên account)
  const roleFromApi =
    userInfo?.tenNhomQuyen ||
    userInfo?.vaiTro ||
    userInfo?.role ||
    userInfo?.nhomQuyen?.ten ||
    null
  const subtitleLabel =
    (typeof roleFromApi === 'string' && roleFromApi.trim() && roleFromApi.trim() !== 'Người dùng'
      ? roleFromApi.trim()
      : null) ||
    profile?.email ||
    userInfo?.email ||
    userInfo?.username ||
    userInfo?.tenDangNhap ||
    null

  const handleUserOpen = (event: React.MouseEvent<HTMLElement>) =>
    setUserAnchor(event.currentTarget)
  const handleUserClose = () => setUserAnchor(null)

  const handleLogout = () => {
    handleUserClose()
    logOut()
  }

  return (
    <AppBar
      position="static"
      color="primary"
      elevation={0}
      sx={{
        position: 'relative',
        zIndex: (t) => t.zIndex.drawer + 10,
        backgroundColor: cv('shell', 'headerSolid'),
        borderBottom: `1px solid ${cv('shell', 'headerBorder')}`,
        boxShadow: cv('shell', 'headerShadow'),
        '& .MuiToolbar-root': {
          alignItems: 'center',
          minHeight: LAYOUT_APP_BAR_HEIGHT_PX,
        },
      }}
    >
      <Toolbar
        sx={{
          width: '100%',
          minHeight: LAYOUT_APP_BAR_HEIGHT_PX,
          paddingLeft: { xs: 0.75, sm: 1.25 },
          paddingRight: { xs: 0.5, sm: 1 },
        }}
      >
        {onToggle && (
          <IconButton
            type="button"
            aria-label={sidebarOpen ? 'Thu gọn menu' : 'Mở rộng menu'}
            onClick={onToggle}
            color="inherit"
            size="medium"
            sx={{
              mr: { xs: 0.5, sm: 1 },
              borderRadius: '6px',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
            }}
          >
            {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        )}
        <Box display="flex" alignItems="center" sx={{ marginRight: 1, flexShrink: 0 }}>
          <img
            src={logoImg}
            alt="AZL Nhân sự"
            style={{
              height: isMobile ? 28 : 32,
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              borderRadius: 6,
            }}
          />
        </Box>
        {pageTitle ? (
          <Typography
            component="h1"
            title={pageTitle}
            sx={{
              flexShrink: 1,
              minWidth: 0,
              maxWidth: { xs: 200, sm: 380, md: 560 },
              ml: { xs: 0.25, sm: 1 },
              pl: { xs: 0.75, sm: 1.5 },
              borderLeft: '1px solid rgba(255, 255, 255, 0.28)',
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              color: 'inherit',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {pageTitle}
          </Typography>
        ) : null}
        <Box flex={1} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <HeaderBranchSwitcher />
          <ThemePickerPopover />
          <Box
            component="button"
            type="button"
            aria-label="user"
            aria-controls="user-menu"
            aria-haspopup="true"
            onClick={handleUserOpen}
            sx={{
              ...HEADER_CONTROL_FRAME_SX,
              px: { xs: 0.75, sm: 1 },
              gap: 0.75,
              cursor: 'pointer',
              color: 'inherit',
              font: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.14)',
              },
            }}
          >
            <PersonOutline fontSize="small" sx={{ flexShrink: 0 }} />
            <Typography
              component="span"
              title={displayUsername}
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: 600,
                lineHeight: 1.25,
                color: 'inherit',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: 88, sm: 140, md: 200 },
                minWidth: 0,
              }}
            >
              {displayUsername}
            </Typography>
          </Box>
          <Menu
            id="user-menu"
            anchorEl={userAnchor}
            open={Boolean(userAnchor)}
            onClose={handleUserClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 0.75,
                minWidth: 240,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 8px 28px rgba(15, 23, 42, 0.16)',
                border: '1px solid',
                borderColor: 'divider',
              },
            }}
            MenuListProps={{ sx: { py: 0 } }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: 'action.hover',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PersonOutline fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  title={displayUsername}
                  sx={{ fontSize: '1rem', lineHeight: 1.3 }}
                >
                  {displayUsername}
                </Typography>
                {subtitleLabel ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={subtitleLabel}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {subtitleLabel}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Divider />
            <MenuItem
              component={Link}
              to={routerConstants.user.changePWD}
              onClick={handleUserClose}
              sx={{ py: 1.35, px: 2, gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <LockOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Đổi mật khẩu"
                primaryTypographyProps={{ variant: 'body1', fontWeight: 500, fontSize: '0.95rem' }}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.35,
                px: 2,
                gap: 1,
                color: 'error.main',
                '& .MuiListItemIcon-root': { color: 'error.main' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Đăng xuất"
                primaryTypographyProps={{ variant: 'body1', fontWeight: 600, fontSize: '0.95rem' }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default HeaderMenu
